import { FormInputProps } from '@/@types/form.types';
import { cn } from '@/lib/utils';
import { Controller } from 'react-hook-form';
import InformationCircle from '@/components/icons/InformationCircle';
import { Button } from '@/components/ui/button';
import { RefObject, useState } from 'react';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuCheckboxItem2,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import ArrowDownIcon from '@/components/icons/ArrowDownIcon';
import { Input } from '@/components/ui/input';
import SearchIcon from '@/components/icons/SearchIcon';

type Props<T extends {}, L extends { id: string; name?: string }> = {
	label?: string;
	list: L[];
	optional?: boolean;
	selectVariant?: 'single' | 'multi';
	searchValue: string;
	setSearchValue: (e: string) => void;
	ref?: RefObject<HTMLDivElement | null>;
	showSearch?: boolean;
	icon?: React.FC<React.SVGProps<SVGSVGElement>>;
} & FormInputProps<T>;

const EListPopupPickerWithSearch = <
	T extends {},
	L extends { id: string; name?: string },
>({
	label,
	placeholder = 'Select from list',
	list,
	optional = false,
	fieldName,
	control,
	containerClassName,
	disabled,
	selectVariant = 'single',
	searchValue,
	setSearchValue,
	ref,
	showSearch = true,
	icon: Icon,
}: Props<T, L>) => {
	const [isOpen, setIsOpen] = useState(false);

	const isOpenToggler = () => {
		setSearchValue(' ');
		setIsOpen((prev) => !prev);
	};

	return (
		<div
			className={cn(
				'w-full flex flex-col gap-1.5 select-none',
				containerClassName,
			)}>
			{label && (
				<h4 className='text-sm font-medium'>
					{label}
					{optional && (
						<span className='text-popover-600'> (optional)</span>
					)}
				</h4>
			)}
			<Controller
				name={fieldName}
				control={control}
				render={({
					field: { value, onChange },
					fieldState,
					formState,
				}) => {
					const isMulti = selectVariant === 'multi';

					const selectedValues = value as L[];

					const toggleValue = (item: L) => {
						if (isMulti) {
							const result = selectedValues.some(
								({ id }) => id === item.id,
							);

							if (result) {
								onChange(
									selectedValues.filter(
										({ id }) => id !== item.id,
									),
								);
							} else {
								onChange([...selectedValues, item]);
							}
						} else {
							const isSelected =
								selectedValues?.length === 1 &&
								selectedValues[0].id === item.id;

							if (isSelected) {
								onChange([]);
							} else {
								onChange([item]);
							}
							isOpenToggler();
						}
					};

					const isSelected = (item: L) =>
						selectedValues?.some(({ id }) => id === item.id);

					return (
						<DropdownMenu
							open={isOpen}
							onOpenChange={isOpenToggler}>
							<DropdownMenuTrigger
								asChild
								disabled={disabled || formState.disabled}
								className={cn(
									'w-full px-4 py-[11px] flex rounded-semi-xl',
									fieldState.invalid
										? 'border-error-1000'
										: '',
									isOpen ? 'border-primary-color-1000' : '',
								)}>
								<Button
									variant='outline'
									disabled={disabled || formState.disabled}
									className='whitespace-normal text-start max-h-fit'>
									<div className='flex items-center gap-2 text-xs text-popover-1000 truncate'>
										{Icon && (
											<Icon className='stroke-popover-1000' />
										)}
										{!!selectedValues?.length ? (
											<span className='truncate'>
												{selectedValues
													.map((value) => value.name)
													.join(', ')}
											</span>
										) : (
											<span className='text-popover-800'>
												{placeholder}
											</span>
										)}
									</div>
									<div
										className={cn(
											'transition-transform duration-50 ease-in-out ml-auto',
											isOpen ? '-rotate-180' : 'rotate-0',
										)}>
										{<ArrowDownIcon />}
									</div>
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent
								className={
									'w-(--radix-dropdown-menu-trigger-width) p-0 rounded-semi-xl border border-popover-100 bg-popover-10'
								}>
								{showSearch && (
									<div className='relative'>
										<Button
											variant='link'
											type='submit'
											className={
												'absolute left-3.5 top-1/2 -translate-y-1/2 h-fit p-0'
											}>
											<SearchIcon />
										</Button>
										<Input
											className={`w-full pl-11 border-0 border-b border-popover-100 rounded-none!`}
											placeholder={'Search'}
											value={
												searchValue === ' '
													? ''
													: searchValue
											}
											onChange={(e) =>
												setSearchValue?.(e.target.value)
											}
										/>
									</div>
								)}
								<div className='h-fit max-h-50 overflow-y-scroll no-scrollbar'>
									{list.map((item) => (
										<DropdownMenuCheckboxItem2
											key={item.id}
											className='px-3.5 py-3'
											checked={isSelected(item)}
											onSelect={(e) => e.preventDefault()}
											onCheckedChange={() =>
												toggleValue(item)
											}>
											{item.name}
										</DropdownMenuCheckboxItem2>
									))}
									<div
										ref={ref}
										className='h-px'
									/>
								</div>
							</DropdownMenuContent>
							{fieldState.invalid && (
								<div className='flex items-center gap-1.5'>
									<InformationCircle className='size-4 stroke-error-1000' />
									<span className='text-xs text-error-1000'>
										{fieldState.error?.message}
									</span>
								</div>
							)}
						</DropdownMenu>
					);
				}}
			/>
		</div>
	);
};
export default EListPopupPickerWithSearch;
